import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { apiService, getActiveCloudSocketUrl, switchToBackupUrl } from '../services/api';
import { io, Socket } from 'socket.io-client';

export interface CartItem {
  menuItemId: string;
  quantity: number;
  price: number;
  name: string;
  breadType?: string;
  selectedAddons?: string[];
  mixAddons?: string[];
  paidAddons?: string[];
  paidAddonsWithQty?: Record<string, number>;
  tempId?: string;
  notes?: string;
  plateType?: string;
  customIngredients?: { name: string; price: number; quantity: number }[];
}

export interface OrderItem {
  menuItemId: string;
  quantity: number;
  price: number;
  name: string;
  breadType?: string;
  selectedAddons?: string[];
  mixAddons?: string[];
  paidAddons?: string[];
  paidAddonsWithQty?: Record<string, number>;
  notes?: string;
  plateType?: string;
  tempId?: string;
  hasExtras?: boolean;
  type?: string;
  customIngredients?: { name: string; price: number; quantity: number }[];
}

export interface Order {
  id?: string;
  _id?: string;
  tableId: number;
  serveurId: string;
  serveurName: string;
  caissierName?: string;
  items: OrderItem[];
  status: 'pending' | 'paid' | 'cancelled' | 'draft';
  total: number;
  ticketNumber: number;
  timestamp: Date;
  paidAt?: Date;
  updatedAt?: Date;
  archived?: boolean;
  type?: 'dine-in' | 'takeaway' | 'delivery';
  clientInfo?: {
    name: string;
    phone: string;
    address: string;
  };
  notes?: string;
  deliveryFee?: number;
  rejectedBy?: string[];
}

export interface Table {
  id: number;
  status: 'available' | 'occupied';
}

export interface MenuItem {
  id: string;
  _id?: string;
  name: string;
  price: number;
  category?: string;
  type: 'sandwich' | 'shawarma' | 'drink' | 'tacos' | 'burger' | 'pizza' | 'plate' | 'side' | 'dessert' | 'mix' | 'other';
  ingredients?: string[];
  plateType?: string;
  recipe?: { stockItem: string | StockItem, amount: number }[];
  branchId?: string;
  image?: string;
}

export interface Addon {
  id: string;
  _id?: string;
  name: string;
  price: number;
  type: 'free' | 'paid';
  category: 'sauce' | 'vegetable' | 'cheese' | 'other' | 'mix' | 'dessert_sauce';
  recipe?: { stockItem: string | StockItem, amount: number }[];
  branchId?: string;
}

export interface Payment {
  id: string;
  orderId: string;
  tableId: number;
  amount: number;
  timestamp: Date;
  caissierName: string;
}

export interface StockItem {
  id: string;
  _id?: string;
  name: string;
  quantity: number;
  unit: string;
  unitPrice?: number;
  minQuantity: number;
  branchId?: string;
}

export interface Supplier {
  id: string;
  _id?: string;
  name: string;
  product: string;
  phone: string;
  totalAmount: number;
  paidAmount: number;
  isPaid: boolean;
  type?: string;
  lastDelivery?: Date;
  amount?: number;
  branchId?: string;
}

export interface Employee {
  id: string;
  _id?: string;
  name: string;
  role?: string;
  salary?: number;
  attendance: { date: string; present: boolean; note?: string }[];
  advances: { date: string; amount: number; note: string }[];
  joinedDate?: string;
  dailyWage: number;
}

export interface User {
  id: string;
  _id?: string;
  username: string;
  name: string;
  role: 'admin' | 'caissier' | 'serveur' | 'client' | 'livreur';
  password?: string;
  phone?: string;
  address?: string;
  branchId?: string;
}

export interface Review {
  id: string;
  orderId?: string;
  name: string;
  rating: number;
  comment: string;
  text?: string;
  timestamp: Date;
}

export interface Expense {
  id: string;
  _id?: string;
  amount: number;
  category: string;
  description: string;
  timestamp: Date;
  type?: string;
  recipientName?: string;
  branchId?: string;
}

interface RestaurantContextType {
  currentUser: User | null;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  tables: Table[];
  setTableStatus: (tableId: number, status: 'available' | 'occupied') => Promise<void>;
  addTable: () => Promise<void>;
  removeTable: () => Promise<void>;
  addSafariSlot: () => Promise<void>;
  removeSafariSlot: () => Promise<void>;
  deleteTable: (number: number) => Promise<void>;
  menuItems: MenuItem[];
  addMenuItem: (item: Omit<MenuItem, 'id'>) => Promise<void>;
  updateMenuItem: (id: string, item: Partial<MenuItem>) => Promise<void>;
  deleteMenuItem: (id: string) => Promise<void>;
  addons: Addon[];
  addAddon: (addon: Omit<Addon, 'id'>) => Promise<void>;
  updateAddon: (id: string, addon: Partial<Addon>) => Promise<void>;
  deleteAddon: (id: string) => Promise<void>;
  orders: Order[];
  addOrder: (order: Omit<Order, 'id' | 'ticketNumber' | 'timestamp'>) => Promise<number>;
  updateOrder: (id: string, update: Partial<Order>) => Promise<any>;
  tableOrdersMap: Record<number, Order[]>;
  getTableOrders: (tableId: number) => Order[];
  processPayment: (tableId: number, caissierName: string, orderIds?: string[]) => Promise<void>;
  payments: Payment[];
  currentTicketNumber: number;
  cashierQueue: { order: Order; addedAt: Date; status: 'new' | 'printing' | 'ready' }[];
  addToCashierQueue: (order: Order) => void;
  clearCashierOrder: (orderId: string, user?: string) => Promise<void>;
  stockItems: StockItem[];
  updateStock: (id: string, update: Partial<StockItem>) => Promise<void>;
  addStockItem: (item: Omit<StockItem, 'id'>) => Promise<void>;
  deleteStockItem: (id: string) => Promise<void>;
  suppliers: Supplier[];
  addSupplier: (supplier: Omit<Supplier, 'id'>) => Promise<void>;
  updateSupplierPaymentStatus: (id: string, isPaid: boolean) => Promise<void>;
  deleteSupplier: (id: string) => Promise<void>;
  employees: Employee[];
  addEmployee: (employee: Omit<Employee, 'id' | 'attendance' | 'advances'>) => Promise<void>;
  markAttendance: (employeeId: string, date: Date, present: boolean) => Promise<void>;
  markAbsent: (employeeId: string, date: Date, note: string) => Promise<void>;
  markPresent: (employeeId: string) => Promise<void>;
  addAdvance: (employeeId: string, amount: number, note: string) => Promise<void>;
  payEmployee: (employeeId: string) => Promise<void>;
  deleteEmployee: (id: string) => Promise<void>;
  users: User[];
  addUser: (user: Omit<User, 'id'>) => Promise<void>;
  updateUser: (id: string, user: Partial<User>) => Promise<void>;
  deleteUser: (id: string) => Promise<void>;
  openCashDrawer: () => Promise<void>;
  cashDrawerLogs: { timestamp: Date; user: string }[];
  expenses: Expense[];
  addExpense: (expense: Omit<Expense, 'id' | 'timestamp'>) => Promise<void>;
  registerClient: (client: Omit<User, 'id' | 'role'>) => Promise<void>;
  reviews: Review[];
  addReview: (review: Omit<Review, 'id' | 'timestamp'>) => Promise<void>;
  refreshData: () => Promise<void>;
  isSocketConnected: boolean;
  lastEvent: string;
  dashboardStats: any;
  selectedBranch: string;
  setSelectedBranch: (branch: string) => void;
  promoBanner: any;
  updatePromoBanner: (banner: any) => Promise<void>;
}

const RestaurantContext = createContext<RestaurantContextType | undefined>(undefined);

const isElectron = typeof window !== 'undefined' && (window as any).process?.type === 'renderer';
const isCapacitor = typeof window !== 'undefined' && (
  (window as any).Capacitor?.isNativePlatform ||
  window.location.href.includes('capacitor://') ||
  window.location.href.includes('http://localhost')
);
const meta = (import.meta as any) || {};
const env = meta.env || {};
const CLOUD_SOCKET = 'https://gambitafood.onrender.com';

const SOCKET_URL = (env.VITE_SOCKET_URL ||
  (isElectron ? 'http://localhost:5000' :
    isCapacitor ? CLOUD_SOCKET :
      (env.DEV ? `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5000` : window.location.origin)));

export const RestaurantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [socketUrl, setSocketUrl] = useState(() => {
    const isElectron = typeof window !== 'undefined' && (window as any).process?.type === 'renderer';
    const isCapacitor = typeof window !== 'undefined' && (
      (window as any).Capacitor?.isNativePlatform ||
      window.location.href.includes('capacitor://') ||
      window.location.href.includes('http://localhost')
    );
    const meta = (import.meta as any) || {};
    const env = meta.env || {};
    return env.VITE_SOCKET_URL ||
      (isElectron ? 'http://localhost:5000' :
        isCapacitor ? getActiveCloudSocketUrl() :
          (env.DEV ? `http://${typeof window !== 'undefined' ? window.location.hostname : 'localhost'}:5000` : window.location.origin));
  });

  const [currentUser, setCurrentUser] = useState<User | null>(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  // Initialize from cache to prevent "white screen" / slowness on startup
  const [tables, setTables] = useState<Table[]>(() => {
    const cached = localStorage.getItem('cache_tables');
    return cached ? JSON.parse(cached) : [];
  });
  const [menuItems, setMenuItems] = useState<MenuItem[]>(() => {
    const cached = localStorage.getItem('cache_menu');
    return cached ? JSON.parse(cached) : [];
  });
  const [addons, setAddons] = useState<Addon[]>(() => {
    const cached = localStorage.getItem('cache_addons');
    return cached ? JSON.parse(cached) : [];
  });
  const [orders, setOrders] = useState<Order[]>(() => {
    const cached = localStorage.getItem('cache_orders');
    return cached ? JSON.parse(cached) : [];
  });
  const [payments, setPayments] = useState<Payment[]>([]);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [promoBanner, setPromoBanner] = useState<any>(() => {
    const cached = localStorage.getItem('cache_promo_banner');
    if (cached) {
      try {
        const banner = JSON.parse(cached);
        let currentBranch = 'gombita-1';
        const stored = localStorage.getItem('user');
        if (stored) {
          const user = JSON.parse(stored);
          if (user.branchId) currentBranch = user.branchId;
        } else {
          const confirmed = localStorage.getItem('confirmed_branch_id');
          if (confirmed) currentBranch = confirmed;
        }
        // Only use cached banner if it belongs to current store
        if (banner.store_id === currentBranch) {
          return banner;
        }
      } catch (e) {
        return null;
      }
    }
    return null;
  });
  const [cashDrawerLogs, setCashDrawerLogs] = useState<{ timestamp: Date; user: string }[]>([]);
  const [currentTicketNumber, setCurrentTicketNumber] = useState(1);
  const [cashierQueue, setCashierQueue] = useState<{ order: Order; addedAt: Date; status: 'new' | 'printing' | 'ready' }[]>([]);
  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const [lastEvent, setLastEvent] = useState('جاهز');
  const [selectedBranch, setSelectedBranch] = useState<string>(() => {
    const stored = localStorage.getItem('user');
    if (stored) {
      try {
        const user = JSON.parse(stored);
        if (user.branchId) return user.branchId;
      } catch (e) { }
    }
    const confirmed = localStorage.getItem('confirmed_branch_id');
    if (confirmed) return confirmed;
    return 'gombita-1';
  });
  const [dashboardStats, setDashboardStats] = useState<any>({ todayRevenue: 0, totalRevenue: 0, todayCount: 0 });
  const [socket, setSocket] = useState<Socket | null>(null);

  const refreshDataRef = useRef<() => void>(() => { });
  const selectedBranchRef = useRef<string>(selectedBranch);

  useEffect(() => {
    selectedBranchRef.current = selectedBranch;
    apiService.setBranchId(selectedBranch);
  }, [selectedBranch]);

  // Re-join socket room when branch changes
  useEffect(() => {
    if (socket && selectedBranch) {
      console.log('Branch changed, re-joining socket room:', selectedBranch);
      socket.emit('join-room', selectedBranch);
      // Refetch promo banner for the new branch
      apiService.getPromoBanner(selectedBranch).then(banner => {
        if (banner && banner.store_id === selectedBranch) {
          setPromoBanner(banner);
          localStorage.setItem('cache_promo_banner', JSON.stringify(banner));
        } else {
          setPromoBanner(null);
          localStorage.removeItem('cache_promo_banner');
        }
      }).catch(err => console.error('Failed to fetch banner after branch change:', err));
    }
  }, [selectedBranch, socket]);

  const refreshData = useCallback(async () => {
    try {
      const storedUserString = localStorage.getItem('user');
      const storedUser = storedUserString ? JSON.parse(storedUserString) : null;
      const isAdmin = storedUser?.role === 'admin';
      const isCaissier = storedUser?.role === 'caissier';

      // Data fetching is now reactive to selectedBranch via useEffect

      // 1. Fetch public/essential data that all roles can access
      const [tablesData, menuData, addonsData, ordersData, reviewsData, promoBannerData] = await Promise.all([
        apiService.getTables().catch(() => []),
        apiService.getMenuItems().catch(() => []),
        apiService.getAddons().catch(() => []),
        apiService.getOrders().catch(() => []),
        apiService.getReviews().catch(() => []),
        apiService.getPromoBanner(selectedBranch).catch(() => null)
      ]);

      setTables(tablesData);
      setMenuItems(menuData);
      setAddons(addonsData);
      setOrders(ordersData.sort((a: any, b: any) => (a.ticketNumber || 0) - (b.ticketNumber || 0)));
      if (reviewsData) setReviews(reviewsData);
      if (promoBannerData) {
        setPromoBanner(promoBannerData);
        localStorage.setItem('cache_promo_banner', JSON.stringify(promoBannerData));
      }

      // Update Cache
      localStorage.setItem('cache_tables', JSON.stringify(tablesData));
      localStorage.setItem('cache_menu', JSON.stringify(menuData));
      localStorage.setItem('cache_addons', JSON.stringify(addonsData));
      localStorage.setItem('cache_orders', JSON.stringify(ordersData));

      if (ordersData.length > 0) {
        const maxTicket = Math.max(...ordersData.map((o: any) => o.ticketNumber || 0));
        setCurrentTicketNumber(maxTicket + 1);
      }

      // 2. Fetch specialized data based on permissions to avoid 403 errors
      if (isAdmin || isCaissier) {
        apiService.getStock().then(setStockItems).catch(err => console.warn('Stock fetch failed:', err.message));
        apiService.getSuppliers().then(data => {
          setSuppliers(data.map((s: any) => ({
            ...s,
            lastDelivery: s.lastDelivery ? new Date(s.lastDelivery) : new Date()
          })));
        }).catch(err => console.warn('Suppliers fetch failed:', err.message));
        apiService.getExpenses().then(setExpenses).catch(err => console.warn('Expenses fetch failed:', err.message));
        apiService.getDashboardStats().then(setDashboardStats).catch(err => console.warn('Stats fetch failed:', err.message));
      }

      if (isAdmin || isCaissier) {
        apiService.getEmployees().then(setEmployees).catch(err => console.warn('Employees fetch failed:', err.message));
        apiService.getUsers().then(setUsers).catch(err => console.warn('Users fetch failed:', err.message));
      }

    } catch (err) {
      console.error('Critical failure in refreshData:', err);
    }
  }, [selectedBranch]);

  useEffect(() => {
    refreshDataRef.current = refreshData;
  }, [refreshData]);

  // Reactively sync apiService and refresh data when branch changes
  useEffect(() => {
    if (selectedBranch) {
      apiService.setBranchId(selectedBranch);
      refreshData();
      if (socket && socket.connected) {
        const storedUserString = localStorage.getItem('user');
        const storedUser = storedUserString ? JSON.parse(storedUserString) : null;
        if (storedUser?.role === 'livreur') {
          console.log(`Switching socket room to ALL branches for livreur`);
          socket.emit('join-room', 'gombita-1');
          socket.emit('join-room', 'gombita-2');
        } else {
          console.log(`Switching socket room to: ${selectedBranch}`);
          socket.emit('join-room', selectedBranch);
        }
      }
    }
  }, [selectedBranch, refreshData, socket]);

  useEffect(() => {
    console.log('🔌 Connecting socket to:', socketUrl);
    const socketInstance = io(socketUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    setSocket(socketInstance);

    socketInstance.on('connect', () => {
      setIsSocketConnected(true);
      const storedUserString = localStorage.getItem('user');
      const storedUser = storedUserString ? JSON.parse(storedUserString) : null;
      if (storedUser?.role === 'livreur') {
        console.log(`Joining all branch rooms for livreur`);
        socketInstance.emit('join-room', 'gombita-1');
        socketInstance.emit('join-room', 'gombita-2');
      } else if (selectedBranchRef.current) {
        console.log(`Joining branch room: ${selectedBranchRef.current}`);
        socketInstance.emit('join-room', selectedBranchRef.current);
      } else {
        // Default to gombita-1 if no branch is selected
        console.log(`No branch selected, joining default room: gombita-1`);
        socketInstance.emit('join-room', 'gombita-1');
      }
      setLastEvent('متصل');
      console.log('✅ Socket connected to:', socketUrl);
    });

    socketInstance.on('connect_error', (err) => {
      console.warn('⚠️ Socket connection error:', err.message);
      setIsSocketConnected(false);
      setLastEvent('خطأ في الاتصال');

      // Fallback: if primary socket failed and we are in Capacitor, switch to backup socket
      const isCapacitor = typeof window !== 'undefined' && (
        (window as any).Capacitor?.isNativePlatform ||
        window.location.href.includes('capacitor://') ||
        window.location.href.includes('http://localhost')
      );
      if (isCapacitor && socketUrl.includes('gombitafood.store')) {
        console.warn('⚠️ Socket connection to primary failed. Switching to backup socket...');
        switchToBackupUrl();
        setSocketUrl(getActiveCloudSocketUrl());
      }
    });

    socketInstance.on('disconnect', (reason) => {
      console.log('❌ Socket disconnected:', reason);
      setIsSocketConnected(false);
      setLastEvent('مقطوع');
    });

    const refreshTimers: Record<string, NodeJS.Timeout> = {};
    const debouncedSliceRefresh = (slice: string) => {
      if (refreshTimers[slice]) clearTimeout(refreshTimers[slice]);
      refreshTimers[slice] = setTimeout(() => {
        refreshDataRef.current();
      }, 500);
    };

    socketInstance.on('new-order', (order) => {
      console.log('Real-time: New Order Received!', order);
      setLastEvent(`طلب جديد #${order.ticketNumber} (${new Date().toLocaleTimeString()})`);
      debouncedSliceRefresh('orders');
    });
    socketInstance.on('order-updated', () => {
      console.log('Real-time: Order Updated! Refreshing...');
      setLastEvent(`تحديث طلب (${new Date().toLocaleTimeString()})`);
      debouncedSliceRefresh('orders');
    });
    socketInstance.on('menu-updated', () => {
      console.log('Real-time: Menu Updated! Refreshing...');
      setLastEvent(`تحديث القائمة (${new Date().toLocaleTimeString()})`);
      debouncedSliceRefresh('menu');
    });
    socketInstance.on('stock-updated', () => {
      console.log('Real-time: Stock Updated! Refreshing...');
      setLastEvent(`تحديث المخزون (${new Date().toLocaleTimeString()})`);
      debouncedSliceRefresh('stock');
    });
    socketInstance.on('table-updated', () => {
      console.log('Real-time: Table Updated! Refreshing...');
      setLastEvent(`تحديث طاولة (${new Date().toLocaleTimeString()})`);
      debouncedSliceRefresh('tables');
    });
    socketInstance.on('daily-reset', () => {
      console.log('Real-time: Daily Reset Triggered!');
      setLastEvent(`إعادة تعيين يومية (${new Date().toLocaleTimeString()})`);
      refreshDataRef.current();
    });
    socketInstance.on('stats-updated', async () => {
      console.log('Real-time: Stats Updated! Refreshing immediately...');
      setLastEvent(`تحديث الإحصائيات (${new Date().toLocaleTimeString()})`);
      try {
        const stats = await apiService.getDashboardStats();
        setDashboardStats(stats);
      } catch (err) {
        console.warn('Stats fetch failed:', err);
      }
    });
    socketInstance.on('promo-banner-updated', (banner: any) => {
      console.log('Real-time: Promo Banner Updated:', banner, 'Current store:', selectedBranchRef.current);
      if (banner && banner.deleted) {
        if (banner.store_id === selectedBranchRef.current) {
          console.log('Banner deleted, clearing for current store');
          setPromoBanner(null);
          localStorage.removeItem('cache_promo_banner');
        }
      } else if (banner && banner.store_id === selectedBranchRef.current) {
        console.log('Updating banner for current store');
        setPromoBanner(banner);
        localStorage.setItem('cache_promo_banner', JSON.stringify(banner));
      } else {
        console.log('Ignoring banner update for different store:', banner?.store_id);
      }
    });

    socketInstance.on('auto-download-report', (data: { csv: string, fileName: string }) => {
      console.log('⏰ Auto-downloading daily report from server trigger...');
      const blob = new Blob([data.csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', data.fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    });

    return () => {
      socketInstance.disconnect();
      setSocket(null);
      Object.values(refreshTimers).forEach(clearTimeout);
    };
  }, [socketUrl]);

  useEffect(() => {
    const pendingOrders = orders
      .filter(o => o.status === 'pending' && o.type !== 'delivery')
      .map(order => ({
        order,
        addedAt: new Date(order.timestamp),
        status: 'new' as const
      }));
    setCashierQueue(pendingOrders);

    const syncedPayments = orders
      .filter(o => o.status === 'paid')
      .map(o => ({
        id: o.id || o._id!,
        orderId: o.id || o._id!,
        tableId: o.tableId,
        amount: o.total,
        timestamp: o.paidAt ? new Date(o.paidAt) : (o.updatedAt ? new Date(o.updatedAt) : new Date(o.timestamp)),
        caissierName: o.caissierName || o.serveurName || 'غير معروف'
      }))
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
    setPayments(syncedPayments);
  }, [orders]);

  const login = async (username: string, password: string) => {
    try {
      const response = await apiService.login(username, password);
      const { user, token } = response;
      setCurrentUser(user);
      localStorage.setItem('user', JSON.stringify(user));
      localStorage.setItem('token', token);

      if (user.branchId) {
        setSelectedBranch(user.branchId);
        apiService.setBranchId(user.branchId);
      }

      await refreshDataRef.current();
      return true;
    } catch (err: any) {
      throw err;
    }
  };

  const logout = () => {
    setCurrentUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
  };

  const setTableStatus = async (tableId: number, status: 'available' | 'occupied') => {
    await apiService.updateTableStatus(tableId, status);
    refreshData();
  };

  const addTable = async () => {
    await apiService.createTable();
    refreshData();
  };

  const removeTable = async () => {
    await apiService.deleteTableLast();
    refreshData();
  };

  const addSafariSlot = async () => {
    await apiService.createSafariSlot();
    refreshData();
  };

  const removeSafariSlot = async () => {
    await apiService.deleteSafariSlotLast();
    refreshData();
  };

  const deleteTable = async (number: number) => {
    await apiService.deleteTableByNumber(number);
    refreshData();
  };

  const addMenuItem = async (item: Omit<MenuItem, 'id'>) => {
    await apiService.createMenuItem(item);
    refreshData();
  };

  const updateMenuItem = async (id: string, item: Partial<MenuItem>) => {
    await apiService.updateMenuItem(id, item);
    refreshData();
  };

  const deleteMenuItem = async (id: string) => {
    await apiService.deleteMenuItem(id);
    refreshData();
  };

  const addAddon = async (addon: Omit<Addon, 'id'>) => {
    await apiService.createAddon(addon);
    refreshData();
  };

  const updateAddon = async (id: string, addon: Partial<Addon>) => {
    await apiService.updateAddon(id, addon);
    refreshData();
  };

  const deleteAddon = async (id: string) => {
    await apiService.deleteAddon(id);
    refreshData();
  };

  const addOrder = async (order: Omit<Order, 'id' | 'ticketNumber' | 'timestamp'>) => {
    const newOrder = await apiService.createOrder(order);
    const syncedOrder = {
      ...newOrder,
      timestamp: new Date(newOrder.timestamp || Date.now())
    };
    setOrders(prev => [...prev, syncedOrder].sort((a, b) => (a.ticketNumber || 0) - (b.ticketNumber || 0)));
    if (newOrder.tableId && Number(newOrder.tableId) > 0) {
      setTables(prev => prev.map(t =>
        t.id === Number(newOrder.tableId) ? { ...t, status: 'occupied' } : t
      ));
    }
    refreshData().catch(err => console.error('Background refresh failed:', err));
    return syncedOrder.ticketNumber;
  };

  const updateOrder = async (id: string, update: Partial<Order>) => {
    const updatedOrder = await apiService.updateOrder(id, update);
    setOrders(prev => prev.map(o => (o.id === id || o._id === id) ? { ...o, ...updatedOrder } : o).sort((a, b) => (a.ticketNumber || 0) - (b.ticketNumber || 0)));
    refreshData().catch(err => console.error('Background refresh failed:', err));
    return updatedOrder;
  };

  const tableOrdersMap = React.useMemo(() => {
    const map: Record<number, Order[]> = {};
    orders.forEach(order => {
      if (order.status === 'pending' && order.archived !== true) {
        const tId = Number(order.tableId);
        if (!map[tId]) map[tId] = [];
        map[tId].push(order);
      }
    });
    return map;
  }, [orders]);

  const getTableOrders = useCallback((tableId: number) => {
    return tableOrdersMap[Number(tableId)] || [];
  }, [tableOrdersMap]);

  const processPayment = async (tableId: number, caissierName: string, orderIds?: string[]) => {
    try {
      const response = await apiService.processPayment(tableId, caissierName, orderIds);
      const serverPaidAt = response?.paidAt ? new Date(response.paidAt) : new Date();
      const serverPaidIds: string[] = (response?.paidOrderIds && response.paidOrderIds.length > 0)
        ? response.paidOrderIds
        : (orderIds || []);

      let recentlyPaidOrders: Order[] = [];
      if (serverPaidIds.length > 0) {
        recentlyPaidOrders = orders.filter(o => {
          const id = o.id || o._id;
          return id && serverPaidIds.includes(id);
        });
        setOrders(prevOrders => prevOrders.map(o => {
          const id = o.id || o._id;
          if (id && serverPaidIds.includes(id)) {
            return { ...o, status: 'paid', caissierName, paidAt: serverPaidAt };
          }
          return o;
        }));
        setCashierQueue(prev => prev.filter(co => {
          const id = co.order.id || co.order._id;
          return !(id && serverPaidIds.includes(id));
        }));
      } else {
        recentlyPaidOrders = orders.filter(o => o.tableId === tableId && o.status === 'pending');
        setOrders(prev => prev.map(o =>
          (o.tableId === tableId && o.status === 'pending')
            ? { ...o, status: 'paid', caissierName, paidAt: serverPaidAt }
            : o
        ));
        setCashierQueue(prev => prev.filter(co => co.order.tableId !== tableId));
      }

      if (recentlyPaidOrders.length > 0) {
        setPayments(prev => {
          const newPayments: Payment[] = recentlyPaidOrders.map(o => ({
            id: o.id || o._id || Math.random().toString(),
            orderId: o.id || o._id || '',
            tableId: Number(o.tableId),
            amount: o.total,
            timestamp: serverPaidAt,
            caissierName: caissierName
          }));
          const existingOrderIds = new Set(prev.map(p => p.orderId));
          const uniqueNewPayments = newPayments.filter(p => !existingOrderIds.has(p.orderId));
          return [...uniqueNewPayments, ...prev].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        });
      }

      if (tableId !== 0) {
        const remainingPending = orders.filter(o =>
          o.tableId === tableId &&
          o.status === 'pending' &&
          !(serverPaidIds.includes(o.id || o._id!))
        );
        if (remainingPending.length === 0) {
          setTables(prev => prev.map(t => t.id === tableId ? { ...t, status: 'available' } : t));
        }
      }

      // Final sync to ensure everything (stats, orders, etc.) is correct
      await refreshData();
    } catch (err) {
      console.error('Payment processing failed:', err);
    }
  };

  const updateStock = async (id: string, update: Partial<StockItem>) => {
    await apiService.updateStock(id, update);
    refreshData();
  };

  const deleteStockItem = async (id: string) => {
    await apiService.deleteStockItem(id);
    refreshData();
  };

  const addStockItem = async (item: Omit<StockItem, 'id'>) => {
    await apiService.createStockItem(item);
    refreshData();
  };

  const addSupplier = async (supplier: Omit<Supplier, 'id'>) => {
    await apiService.createSupplier(supplier);
    refreshData();
  };

  const updateSupplierPaymentStatus = async (id: string, isPaid: boolean) => {
    await apiService.updateSupplier(id, { isPaid });
    refreshData();
  };

  const deleteSupplier = async (id: string) => {
    await apiService.deleteSupplier(id);
    refreshData();
  };

  const addEmployee = async (employee: Omit<Employee, 'id' | 'attendance' | 'advances'>) => {
    await apiService.createEmployee(employee);
    refreshData();
  };

  const markAttendance = async (employeeId: string, date: Date, present: boolean) => {
    await apiService.markAttendance(employeeId, date, present);
    refreshData();
  };

  const markAbsent = async (employeeId: string, date: Date, note: string) => {
    await apiService.markAbsent(employeeId, date, note);
    refreshData();
  };

  const markPresent = async (employeeId: string) => {
    await apiService.markPresent(employeeId);
    refreshData();
  };

  const addAdvance = async (employeeId: string, amount: number, note: string) => {
    await apiService.addAdvance(employeeId, amount, note);
    refreshData();
  };

  const payEmployee = async (employeeId: string) => {
    await apiService.payEmployee(employeeId);
    refreshData();
  };

  const deleteEmployee = async (id: string) => {
    await apiService.deleteEmployee(id);
    refreshData();
  };

  const addUser = async (user: Omit<User, 'id'>) => {
    try {
      await apiService.createUser(user);
      refreshDataRef.current();
    } catch (err) {
      throw err;
    }
  };

  const updateUser = async (id: string, user: Partial<User>) => {
    try {
      await apiService.updateUser(id, user);
      refreshDataRef.current();
    } catch (err) {
      throw err;
    }
  };

  const deleteUser = async (id: string) => {
    await apiService.deleteUser(id);
    refreshData();
  };

  const registerClient = async (client: Omit<User, 'id' | 'role'>) => {
    try {
      await apiService.createUser({ ...client, role: 'client' });
      await login(client.username, client.password || '');
      refreshData();
    } catch (err: any) {
      console.error('Registration failed:', err.message);
      throw err;
    }
  };

  const addReview = async (review: Omit<Review, 'id' | 'timestamp'>) => {
    await apiService.createReview(review);
    refreshData();
  };

  const addExpense = async (expense: Omit<Expense, 'id' | 'timestamp'>) => {
    await apiService.createExpense(expense);
    refreshData();
  };

  const openCashDrawer = async () => {
    if (currentUser) {
      try {
        await apiService.openCashDrawer();
        setCashDrawerLogs(prev => [...prev, { timestamp: new Date(), user: currentUser.name }]);
      } catch (err) {
        console.error('Failed to open drawer:', err);
      }
    }
  };

  const addToCashierQueue = (order: Order) => {
    setCashierQueue(prev => [...prev, { order, addedAt: new Date(), status: 'new' }]);
  };

  const clearCashierOrder = async (orderId: string, user?: string) => {
    try {
      await apiService.deleteOrder(orderId, user);
      refreshData();
    } catch (err) {
      console.error('Failed to delete order:', err);
    }
  };

  const updatePromoBanner = useCallback(async (banner: any) => {
    await apiService.updatePromoBanner(banner);
    setPromoBanner(banner);
    localStorage.setItem('cache_promo_banner', JSON.stringify(banner));
  }, []);

  const value = useMemo(() => ({
    currentUser, login, logout,
    tables, setTableStatus, addTable, removeTable, addSafariSlot, removeSafariSlot, deleteTable,
    menuItems, addMenuItem, updateMenuItem, deleteMenuItem,
    addons, addAddon, updateAddon, deleteAddon,
    orders, addOrder, updateOrder, tableOrdersMap, getTableOrders, processPayment,
    payments,
    currentTicketNumber,
    cashierQueue, addToCashierQueue, clearCashierOrder,
    stockItems, updateStock, addStockItem, deleteStockItem,
    suppliers, addSupplier, updateSupplierPaymentStatus, deleteSupplier,
    employees, addEmployee, markAttendance, markAbsent, markPresent, addAdvance, payEmployee, deleteEmployee,
    users, addUser, updateUser, deleteUser,
    openCashDrawer, cashDrawerLogs,
    expenses, addExpense,
    registerClient,
    reviews,
    addReview,
    refreshData,
    isSocketConnected,
    lastEvent,
    dashboardStats,
    selectedBranch,
    setSelectedBranch,
    promoBanner,
    updatePromoBanner
  }), [
    currentUser, login, logout,
    tables, setTableStatus, addTable, removeTable, addSafariSlot, removeSafariSlot, deleteTable,
    menuItems, addMenuItem, updateMenuItem, deleteMenuItem,
    addons, addAddon, updateAddon, deleteAddon,
    orders, addOrder, updateOrder, tableOrdersMap, getTableOrders, processPayment,
    payments,
    currentTicketNumber,
    cashierQueue, addToCashierQueue, clearCashierOrder,
    stockItems, updateStock, addStockItem, deleteStockItem,
    suppliers, addSupplier, updateSupplierPaymentStatus, deleteSupplier,
    employees, addEmployee, markAttendance, markAbsent, markPresent, addAdvance, payEmployee, deleteEmployee,
    users, addUser, updateUser, deleteUser,
    openCashDrawer, cashDrawerLogs,
    expenses, addExpense,
    registerClient,
    reviews,
    addReview,
    refreshData,
    isSocketConnected,
    lastEvent,
    dashboardStats,
    selectedBranch,
    setSelectedBranch,
    promoBanner,
    updatePromoBanner
  ]);

  return <RestaurantContext.Provider value={value}>{children}</RestaurantContext.Provider>;
};

export const useRestaurant = () => {
  const context = useContext(RestaurantContext);
  if (!context) {
    throw new Error('useRestaurant must be used within RestaurantProvider');
  }
  return context;
};

